import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { Tooltip } from '../../../components/Tooltip';
import { PreviewTable } from '../../../components/PreviewTable';
import { InlineAddTable, InlineRow } from '../../../components/inline-add-table/InlineAddTable';
import { TdMoney, TdSelect } from '../../../components/inline-add-table/cells';
import { FormFieldset } from '../../../components/inputs/FormFieldset';
import { Select, type SelectOption } from '../../../components/inputs/Select';
import { Textarea } from '../../../components/inputs/Textarea';
import { ApiError } from '../../../lib/apiError';
import { formatMoney } from '../../../lib/formatMoney';
import { uid } from '../../../lib/uid';
import { focusValidationError } from '../../../lib/validationFocus';
import { usePersonNames } from '../../human-resources/personnel/usePersonNames';
import { useStaffOptions } from '../../human-resources/personnel/useStaff';
import { cashCatalogsApi } from '../cashCatalogsApi';
import type { CashCatalogOption, CashCatalogSlug } from '../cashCatalogTypes';
import { disbursementRequestsApi } from '../disbursementRequestsApi';
import { particularsSuggestions } from '../particularsSuggestions';
import type { DisbursementRequestDetail, DisbursementRequestItemInput } from '../disbursementRequestTypes';
import { sampleDisbursementRequest } from '../utils/sampleDisbursementRequest';

const ID_PREFIX = 'disbursement-request';

/** `amount` to `amount`, `particulars` to `particulars` — exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `disbursement-request-items.2.amount`. A CONTRACT with the
 * server, which names a rejected line `items.2.amount`; `focusValidationError` joins the two.
 */
const cellId = (index: number, field: string) => `${ID_PREFIX}-items.${index}.${kebab(field)}`;

interface DraftLine {
  key: string;
  particulars: string;
  amount: number | null;
}

const EMPTY = (): DraftLine => ({ key: uid(), particulars: '', amount: null });

/** A row nobody touched — the same test the server's `IsBlank` makes. */
const isBlank = (line: DraftLine) => line.particulars.trim() === '' && line.amount == null;

/** A row somebody finished. */
const isFilled = (line: DraftLine) => line.particulars.trim() !== '' && line.amount != null && line.amount > 0;

export interface SelfServiceBody {
  transactionTypeId: string | null;
  paymentTypeId: string | null;
  items: DisbursementRequestItemInput[];
  notes?: string | null;
}

export interface SelfServiceSource {
  /** Stated in place of the picker — the employee number of the caller's own record. */
  requesterLabel: string;
  loadCatalog: (catalog: CashCatalogSlug, signal: AbortSignal) => Promise<CashCatalogOption[]>;
  create: (body: SelfServiceBody) => Promise<DisbursementRequestDetail>;
  update: (id: string, body: SelfServiceBody) => Promise<DisbursementRequestDetail>;
}

/**
 * Raise a request to the Cash Division, or correct one still waiting on it.
 *
 * The fields read in the order the request is thought about: who it is for, what kind of
 * transaction, how it is paid, the particulars line by line, and remarks.
 *
 * <b>The particulars are LINES, each with its own amount</b>, and the request's amount is their
 * total — stated under the table, never typed. The draft total is this form's own arithmetic;
 * once saved, every surface reads the server's sum.
 *
 * <b>The requesting party is PICKED</b> on the Cash Division's form — Cash encodes on an
 * employee's behalf. <b>`self` turns it into the Self Service form</b>: the requester is then
 * STATED (the server derives it from the caller's own employee record), the staff list is never
 * loaded, and the pickers and the save go through the caller's own endpoints.
 *
 * <b>A retired catalog row already on the request is kept as an option</b>, so the field states
 * what the record says rather than falling back to its placeholder.
 */
export function DisbursementRequestModal({
  open,
  editing,
  onClose,
  onSaved,
  self,
}: {
  open: boolean;
  /** Null creates. */
  editing: DisbursementRequestDetail | null;
  onClose: () => void;
  onSaved: (saved: DisbursementRequestDetail) => void;
  /** Present on the Self Service surface — see the component's own remarks. */
  self?: SelfServiceSource;
}) {
  const [requesterId, setRequesterId] = useState<string | null>(editing?.requesterPersonnelId ?? null);
  const [transactionTypeId, setTransactionTypeId] = useState<string | null>(editing?.transactionTypeId ?? null);
  const [paymentTypeId, setPaymentTypeId] = useState<string | null>(editing?.paymentTypeId ?? null);
  const [lines, setLines] = useState<DraftLine[]>(() =>
    editing != null && editing.items.length > 0
      ? editing.items.map((i) => ({ key: uid(), particulars: i.particulars, amount: i.amount }))
      : [EMPTY()],
  );
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [transactionOptions, setTransactionOptions] = useState<SelectOption[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<SelectOption[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [catalogsFailed, setCatalogsFailed] = useState(false);
  // Never loaded on the Self Service form: it needs `personnel.read`, which an employee lacks.
  const { options: staff, loading: loadingStaff } = useStaffOptions(open && self == null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const loadCatalog = self?.loadCatalog;

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    const load = loadCatalog ?? cashCatalogsApi.options;

    Promise.all([load('transaction-types', controller.signal), load('payment-types', controller.signal)])
      .then(([transactions, payments]) => {
        if (controller.signal.aborted) return;

        setTransactionOptions(transactions.map((o) => ({ value: o.id, label: o.name })));
        setPaymentOptions(payments.map((o) => ({ value: o.id, label: o.name })));
      })
      /*
        A failed picker load names no field, so it stays out of the save path's error state —
        but it is SAID, or the reader meets two empty required pickers and a dead Submit with
        nothing explaining either.
      */
      .catch(() => {
        if (!controller.signal.aborted) setCatalogsFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCatalogs(false);
      });

    return () => controller.abort();
  }, [open, loadCatalog]);

  // The value the record already carries stays offered even when it has since been retired.
  const transactionChoices = useMemo(
    () => withCurrent(transactionOptions, editing?.transactionTypeId, editing?.transactionTypeName),
    [transactionOptions, editing?.transactionTypeId, editing?.transactionTypeName],
  );
  /*
    THE USUAL PARTICULARS FOR THE CHOSEN TYPE, offered on every line. Read off the type's NAME
    (`particularsSuggestions`), so a type with none — or no type chosen yet — leaves the cell a
    plain typed value.
  */
  const suggestions = useMemo(
    () => particularsSuggestions(transactionChoices.find((o) => o.value === transactionTypeId)?.label),
    [transactionChoices, transactionTypeId],
  );

  const paymentChoices = useMemo(
    () => withCurrent(paymentOptions, editing?.paymentTypeId, editing?.paymentTypeName),
    [paymentOptions, editing?.paymentTypeId, editing?.paymentTypeName],
  );

  /*
    THE CURRENT REQUESTER STAYS OFFERED on an edit, as a retired catalog row does: the staff list
    carries active employees only (and the first hundred of them), so a requester since
    deactivated would otherwise leave the field holding an id it cannot show — and a clerk
    "filling" it would move the request to somebody else.
  */
  const currentRequesterPersonIds = useMemo(
    () => (self == null && editing?.requesterPersonId != null ? [editing.requesterPersonId] : []),
    [self, editing?.requesterPersonId],
  );
  const currentRequesterNames = usePersonNames(currentRequesterPersonIds);
  const requesterChoices = useMemo(() => {
    if (editing == null || staff.some((o) => o.value === editing.requesterPersonnelId)) return staff;

    const name =
      editing.requesterPersonId != null ? currentRequesterNames.nameOf(editing.requesterPersonId) : null;

    return [
      ...staff,
      { value: editing.requesterPersonnelId, label: name ?? 'Current requesting party', sublabel: 'Not in the active list' },
    ];
  }, [staff, editing, currentRequesterNames]);

  const filledCount = lines.filter(isFilled).length;
  // Every line is either finished or untouched — a half-filled one holds the form back.
  const linesValid = filledCount > 0 && lines.every((l) => isFilled(l) || isBlank(l));

  /*
    THE DRAFT TOTAL. An unsaved request has no server figure to read, so the form adds up what
    is on screen — once, here. Once saved, every surface reads the server's sum instead.
  */
  const draftTotal = useMemo(() => lines.reduce((sum, l) => sum + (isFilled(l) ? (l.amount ?? 0) : 0), 0), [lines]);

  const valid = (self != null || requesterId != null) && transactionTypeId != null && paymentTypeId != null && linesValid;
  const canSave = valid && confirmed && !saving;

  /*
    WHY SUBMIT IS DISABLED, first thing missing first — the purchase request form's own shape.
    A half-filled line has no error of its own in the grid, so without this sentence the reader
    meets a dead button over a form that looks complete.
  */
  const saveDisabledReason = saving
    ? null
    : self == null && requesterId == null
      ? 'Choose the requesting party.'
      : transactionTypeId == null
        ? 'Choose the transaction type.'
        : paymentTypeId == null
          ? 'Choose the payment type.'
          : filledCount === 0
            ? 'Add a line with its particulars and an amount more than zero.'
            : !linesValid
              ? 'Every line needs its particulars and an amount more than zero. Remove the empty one, or fill it in.'
              : !confirmed
                ? 'Tick the confirm box to submit.'
                : null;

  function patch(key: string, change: Partial<DraftLine>) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const body: SelfServiceBody = {
      transactionTypeId,
      paymentTypeId,
      // EVERY row is sent, blanks included, so a rejection's index is the row on screen.
      items: lines.map((l) => ({
        particulars: l.particulars.trim(),
        amount: l.amount == null ? null : Math.round(l.amount * 100) / 100,
      })),
      notes: notes.trim() || null,
    };
    const request = { ...body, requesterPersonnelId: requesterId };

    try {
      const saved = self
        ? editing
          ? await self.update(editing.id, body)
          : await self.create(body)
        : editing
          ? await disbursementRequestsApi.update(editing.id, request)
          : await disbursementRequestsApi.create(request);

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this request.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // The refusals that name the BATCH — nothing to add, the cap, the total — are answered at the `+`.
        fieldIds: { items: `${ID_PREFIX}-add-line` },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleDisbursementRequest({
      staff,
      transactionTypes: transactionChoices,
      paymentTypes: paymentChoices,
    });

    if (sample.requesterPersonnelId) setRequesterId(sample.requesterPersonnelId);
    if (sample.transactionTypeId) setTransactionTypeId(sample.transactionTypeId);
    if (sample.paymentTypeId) setPaymentTypeId(sample.paymentTypeId);
    setLines(sample.items.map((i) => ({ key: uid(), particulars: i.particulars, amount: i.amount })));
    setNotes(sample.notes);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.requestNumber}` : 'New request'}
      size="2xl"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || (self == null && loadingStaff) || loadingCatalogs }}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {catalogsFailed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              The transaction and payment types could not be loaded. Close this form and open it again.
            </p>
          </div>
        ) : null}

        {self ? (
          /*
            STATED, not offered: the server derives the requesting party from the caller's own
            employee record, so a picker here could only ever hold one answer.
          */
          <PreviewTable variant="table-display" rows={[{ label: 'Requesting party', value: self.requesterLabel }]} />
        ) : null}

        {/*
          ONE FIELD PER ROW — product call, 09 Oct 2026. The three answer one question each and
          are read top to bottom, in the order the request is thought about.
        */}
        <div className="flex flex-col gap-4">
          {self ? null : (
            <Select
              id={`${ID_PREFIX}-requester-personnel-id`}
              label="Requesting party"
              required
              description="The employee the disbursement is for. Only active employees are offered."
              value={requesterId}
              onChange={(next) => setRequesterId(next || null)}
              options={requesterChoices}
              loading={loadingStaff}
              placeholder="Choose an employee"
              remote
              error={requesterId != null ? error?.fieldError('requesterPersonnelId') : 'Choose the requesting party.'}
              disabled={saving}
            />
          )}

          <Select
            id={`${ID_PREFIX}-transaction-type-id`}
            label="Transaction type"
            required
            description="What the disbursement is for. The list is kept under Cash Division → Settings."
            value={transactionTypeId}
            onChange={(next) => setTransactionTypeId(next || null)}
            options={transactionChoices}
            loading={loadingCatalogs}
            placeholder="Choose a transaction type"
            remote
            error={transactionTypeId != null ? error?.fieldError('transactionTypeId') : 'Choose the transaction type.'}
            disabled={saving}
          />

          <Select
            id={`${ID_PREFIX}-payment-type-id`}
            label="Payment type"
            required
            description="How it will be paid out — EMDS (Landbank), LD.ADA or cheque."
            value={paymentTypeId}
            onChange={(next) => setPaymentTypeId(next || null)}
            options={paymentChoices}
            loading={loadingCatalogs}
            placeholder="Choose a payment type"
            remote
            error={paymentTypeId != null ? error?.fieldError('paymentTypeId') : 'Choose how it will be paid.'}
            disabled={saving}
          />
        </div>

        <FormFieldset legend="Particulars">

          <InlineAddTable
            headers={['Particulars *', { label: 'Amount *', align: 'right' }]}
            colWidths={[460, 180]}
            resizeKey={`${ID_PREFIX}-items-v1`}
            flexColumn={0}
            addButtonId={`${ID_PREFIX}-add-line`}
            addDisabled={saving || lines.some((l) => !isFilled(l))}
            addDisabledReason="Finish the current line before adding another."
            rowCount={lines.length}
            onAddRow={() => setLines((current) => [...current, EMPTY()])}
            /*
              THE ESTIMATED TOTAL, INSIDE THE TABLE — product call, 09 Oct 2026, the purchase
              order form's footer shape. It is this form's own arithmetic over the finished lines;
              once saved, every surface reads the server's sum.
            */
            footerContent={
              <div className="flex items-center justify-end gap-3 pr-1">
                <span className="text-xs text-slate-500">
                  Estimated total · {filledCount} {filledCount === 1 ? 'line' : 'lines'}
                </span>
                <span id={`${ID_PREFIX}-total`} className="font-mono text-sm font-medium tabular-nums text-slate-800">
                  {formatMoney(draftTotal)}
                </span>
              </div>
            }
          >
            {lines.map((line, index) => (
              <InlineRow
                key={line.key}
                idx={index + 1}
                onRemove={() => setLines((current) => current.filter((l) => l.key !== line.key))}
              >
                {/*
                  A DROPDOWN OF SUGGESTIONS THAT STILL TAKES ANY TEXT — `allowCustom` with no
                  create handler, the stock adjustment reason's shape: there is no catalog behind
                  a particular, so the typed words are the value. The line's own value stays
                  offered, so an edit opens on what was written rather than on an empty cell.
                */}
                <TdSelect
                  id={cellId(index, 'particulars')}
                  ariaLabel={`Particulars, line ${index + 1}`}
                  value={line.particulars === '' ? null : line.particulars}
                  onChange={(next) => patch(line.key, { particulars: next })}
                  options={[
                    ...(line.particulars !== '' && !suggestions.includes(line.particulars)
                      ? [line.particulars]
                      : []),
                    ...suggestions,
                  ].map((value) => ({ value, label: value }))}
                  allowCustom
                  sort="none"
                  disabled={saving}
                />
                <TdMoney
                  id={cellId(index, 'amount')}
                  ariaLabel={`Amount, line ${index + 1}`}
                  value={line.amount}
                  onChange={(next) => patch(line.key, { amount: next })}
                  disabled={saving}
                />
              </InlineRow>
            ))}
          </InlineAddTable>

          {error?.fieldError('items') ? (
            <p className="text-xs text-red-700">{error.fieldError('items')}</p>
          ) : null}
        </FormFieldset>

        <Textarea
          id={`${ID_PREFIX}-notes`}
          label="Remarks"
          description="Anything the Cash Division should know. It stays on the request."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={error?.fieldError('notes')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}

/** The options, plus the record's own value where the list no longer carries it. */
function withCurrent(options: SelectOption[], id: string | undefined, name: string | undefined): SelectOption[] {
  if (id == null || name == null || options.some((o) => o.value === id)) return options;

  return [...options, { value: id, label: name, sublabel: 'No longer in use' }];
}
